import React from 'react';
import { infoPageStyles as styles } from './sharedStyles';

const PrivacyPolicy = () => {
  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Privacy Policy</h1>
        <p style={styles.headerSubtitle}>Last updated: November 5, 2025</p>
      </div>

      <div style={styles.contentContainer}>
        <div style={styles.section}>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>Key Points:</strong> Redbird Markets NEVER stores credit card numbers or payment data. All payments are 
              processed by Stripe, Inc., a PCI-DSS Level 1 certified payment processor. This site is hosted in the United States. 
              By using our platform, you consent to data transfer and processing in the U.S.
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Information We Collect</h2>
          <h3 style={styles.subsectionTitle}>Account Information</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Name and ISU email address</li>
            <li style={styles.listItem}>Profile photo (optional)</li>
            <li style={styles.listItem}>Bio and profile information</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Listing and Transaction Data</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Items you list for sale</li>
            <li style={styles.listItem}>Purchase history</li>
            <li style={styles.listItem}>Messages with other users</li>
            <li style={styles.listItem}>Transaction details (amounts, dates, status) - NOT credit card or payment method data</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Usage Data</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Pages visited and features used</li>
            <li style={styles.listItem}>Search queries</li>
            <li style={styles.listItem}>Device and browser information</li>
            <li style={styles.listItem}>IP address and location data</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. How We Use Your Information</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>Provide, maintain, and improve our services</li>
            <li style={styles.listItem}>Process transactions through Stripe (we never handle payment data directly)</li>
            <li style={styles.listItem}>Send notifications about your account, transactions, and platform activities</li>
            <li style={styles.listItem}>Verify ISU affiliation through email verification</li>
            <li style={styles.listItem}>Prevent fraud, abuse, and enhance platform security</li>
            <li style={styles.listItem}>Respond to support requests and communications</li>
            <li style={styles.listItem}>Analyze usage patterns to improve user experience</li>
            <li style={styles.listItem}>Comply with legal obligations and law enforcement requests</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Payment Data and Stripe Processing</h2>
          <div style={styles.highlight}>
            <h3 style={styles.subsectionTitle}>Redbird Markets NEVER Stores Payment Information</h3>
            <p style={styles.text}>
              <strong>CRITICAL:</strong> Redbird Markets does NOT store, process, or have access to your credit card numbers, 
              CVV codes, or full payment details. All payment processing is handled exclusively by Stripe, Inc., a PCI-DSS 
              Level 1 certified payment processor.
            </p>
          </div>
          <p style={styles.text}>How transaction data is handled:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Card data never touches our servers</strong> - Credit card information goes directly to Stripe</li>
            <li style={styles.listItem}><strong>Stripe manages all PCI compliance</strong> - We do not handle sensitive payment data</li>
            <li style={styles.listItem}><strong>We only receive transaction IDs</strong> - Used to track purchases and payouts</li>
            <li style={styles.listItem}>We store: transaction amounts, dates, buyer/seller IDs, and payment status only</li>
            <li style={styles.listItem}>Stripe's privacy policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>https://stripe.com/privacy</a></li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Information Sharing and Third-Party Services</h2>
          <p style={styles.text}>We do not sell your personal information. We share information only as described below:</p>
          
          <h3 style={styles.subsectionTitle}>With Other Users</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your name, profile photo, and listings are visible to other platform users</li>
            <li style={styles.listItem}>Transaction partners can see necessary contact and meeting information</li>
            <li style={styles.listItem}>Your email address is NOT publicly visible</li>
          </ul>

          <h3 style={styles.subsectionTitle}>With Third-Party Service Providers</h3>
          <p style={styles.text}>We use the following third-party services that may access your data:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Stripe, Inc.</strong> - Payment processing (PCI-DSS certified)</li>
            <li style={styles.listItem}><strong>Google Firebase</strong> - Hosting, authentication, database, and cloud storage</li>
            <li style={styles.listItem}><strong>Google Analytics</strong> - Usage analytics and performance monitoring (if enabled)</li>
          </ul>

          <h3 style={styles.subsectionTitle}>With Illinois State University</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>We may share information as required to maintain platform integrity</li>
            <li style={styles.listItem}>Serious policy violations may be reported to ISU Administration</li>
            <li style={styles.listItem}>We comply with university policies and requests when appropriate</li>
          </ul>

          <h3 style={styles.subsectionTitle}>For Legal Reasons</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>To comply with law enforcement requests, subpoenas, or court orders</li>
            <li style={styles.listItem}>To protect rights, property, or safety of Redbird Markets, users, or the public</li>
            <li style={styles.listItem}>To enforce our Terms of Service or investigate violations</li>
            <li style={styles.listItem}>In connection with legal proceedings or governmental requests</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Data Security</h2>
          <p style={styles.text}>
            We implement industry-standard security measures to protect your information:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Encrypted data transmission (HTTPS/TLS)</li>
            <li style={styles.listItem}>Secure password storage through Firebase Authentication (passwords are hashed and salted)</li>
            <li style={styles.listItem}>Regular security audits and updates</li>
            <li style={styles.listItem}>Limited employee/operator access to personal data</li>
            <li style={styles.listItem}>Firebase security rules protecting database access</li>
            <li style={styles.listItem}>PCI-DSS Level 1 compliant payment processing through Stripe (we never store card data)</li>
          </ul>
          <p style={styles.text}>
            While we take reasonable precautions, no system is 100% secure. We cannot guarantee absolute security of data transmitted 
            over the internet.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Data Breach Notification</h2>
          <p style={styles.text}>
            In the event of a data breach that compromises your personal information, we will:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Notify affected users via email within 72 hours of discovery</li>
            <li style={styles.listItem}>Post a notice on the platform homepage</li>
            <li style={styles.listItem}>Report the breach to ISU IT/Security if university data is affected</li>
            <li style={styles.listItem}>Provide information on steps users can take to protect themselves</li>
            <li style={styles.listItem}>Comply with all applicable data breach notification laws</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Your Rights and Choices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Access:</strong> View your personal information in your profile page</li>
            <li style={styles.listItem}><strong>Update:</strong> Edit your profile information (name, photo, bio) through the Edit Profile page</li>
            <li style={styles.listItem}><strong>Delete:</strong> <a href="/contact-us" style={styles.link}>Contact us</a> to request account deletion (some data retained for legal/operational purposes)</li>
            <li style={styles.listItem}><strong>Export:</strong> <a href="/contact-us" style={styles.link}>Contact us</a> to request a copy of your data in portable format</li>
          </ul>
          <p style={styles.text}>
            To exercise these rights, <a href="/contact-us" style={styles.link}>contact us</a> for assistance.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Cookies and Tracking Technologies</h2>
          <p style={styles.text}>
            We use cookies, local storage, and similar technologies to:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Keep you logged in (authentication cookies)</li>
            <li style={styles.listItem}>Track notification visit timestamps to show unread counts</li>
            <li style={styles.listItem}>Analyze site usage and performance (if analytics enabled)</li>
            <li style={styles.listItem}>Improve user experience and platform functionality</li>
            <li style={styles.listItem}>Detect and prevent fraud or security issues</li>
          </ul>
          <p style={styles.text}>
            You can control cookies through your browser settings, but disabling them may affect platform functionality. 
            Essential cookies required for login and security cannot be disabled.
          </p>
          <p style={styles.text}>
            <strong>Types of Cookies We Use:</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Essential:</strong> Required for authentication and core functionality</li>
            <li style={styles.listItem}><strong>Functional:</strong> Track notification timestamps and UI state</li>
            <li style={styles.listItem}><strong>Analytics:</strong> Track usage patterns (Google Analytics, if enabled)</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Data Retention and Deletion</h2>
          <p style={styles.text}>
            <strong>Retention Periods:</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Active Accounts:</strong> Data retained while account is active</li>
            <li style={styles.listItem}><strong>After Account Deletion:</strong> Most data deleted within 30 days</li>
            <li style={styles.listItem}><strong>Transaction Records:</strong> Retained for 12 months after account deletion (legal requirement)</li>
            <li style={styles.listItem}><strong>Messages:</strong> Deleted within 30 days of account closure</li>
            <li style={styles.listItem}><strong>Payment Data:</strong> Never stored by us - managed by Stripe per their retention policy</li>
            <li style={styles.listItem}><strong>Legal Holds:</strong> Data subject to legal proceedings retained until resolved</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Listing Archive System</h3>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>Smart Data Management:</strong> To maintain transaction integrity while keeping the platform organized, 
              we use a two-tier archive system for listings:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}><strong>User Archive:</strong> When you archive a listing, it's hidden from the marketplace but preserved for transaction history</li>
              <li style={styles.listItem}><strong>Automatic Deep Archive:</strong> Listings archived for 90+ days are automatically moved to deep storage (still preserved but fully hidden)</li>
              <li style={styles.listItem}><strong>Transaction Protection:</strong> Archived listings are never deleted to maintain payment records and buyer-seller communication history</li>
              <li style={styles.listItem}><strong>Data Integrity:</strong> Your transaction history remains accessible regardless of listing archive status</li>
            </ul>
          </div>
          <p style={styles.text}>
            Some data may be retained longer for:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Compliance with legal obligations (taxes, audits, subpoenas)</li>
            <li style={styles.listItem}>Fraud prevention and security purposes</li>
            <li style={styles.listItem}>Dispute resolution (transaction metadata)</li>
            <li style={styles.listItem}>Backups (purged within 30 days)</li>
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>10. International Users and Data Transfer</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>United States Hosting:</strong> Redbird Markets is hosted in the United States using Google Firebase services. 
              All data is stored and processed on servers located in the U.S. By using our platform, you consent to the transfer, 
              storage, and processing of your information in the United States, which may have different data protection laws than 
              your country of residence.
            </p>
          </div>
          <p style={styles.text}>
            If you are accessing the platform from outside the United States, you acknowledge that your information will be transferred 
            to and processed in the U.S. in accordance with this Privacy Policy and U.S. law.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Children's Privacy</h2>
          <p style={styles.text}>
            Redbird Markets is intended for users 18 years or older (or with parental consent). We do not knowingly collect personal 
            information from children under 13 years of age in compliance with COPPA (Children's Online Privacy Protection Act).
          </p>
          <p style={styles.text}>
            If we discover that we have inadvertently collected information from a child under 13, we will delete it immediately. 
            If you believe a child has provided us with personal information, please <a href="/contact-us" style={styles.link}>contact us</a>.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>12. University Affiliation Disclaimer</h2>
          <div style={styles.highlight}>
            <p style={styles.text}>
              <strong>NOT AFFILIATED WITH ILLINOIS STATE UNIVERSITY:</strong> Redbird Markets is an independent student project and is 
              NOT affiliated with, endorsed by, or approved by Illinois State University. We are not part of the official university 
              infrastructure and operate independently. Illinois State University is not responsible for this platform, its data practices, 
              or user activities.
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Legal Requests and Compliance</h2>
          <p style={styles.text}>
            We may disclose your information to comply with:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Law enforcement requests and subpoenas</li>
            <li style={styles.listItem}>Court orders and legal processes</li>
            <li style={styles.listItem}>Government investigations and regulations</li>
            <li style={styles.listItem}>University administrative requests (when appropriate)</li>
            <li style={styles.listItem}>Legal obligations under federal, state, or local law</li>
          </ul>
          <p style={styles.text}>
            When legally permitted, we will notify users before disclosing information in response to legal requests.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>14. Changes to This Policy</h2>
          <p style={styles.text}>
            We may update this Privacy Policy from time to time. Material changes will be communicated via:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Updated "Last Updated" date at the top of this page</li>
            <li style={styles.listItem}>Email notification to registered users</li>
            <li style={styles.listItem}>In-platform notifications or banners</li>
            <li style={styles.listItem}>Announcement on homepage for significant changes</li>
          </ul>
          <p style={styles.text}>
            We maintain date-stamped versions of all policies in our version control system (GitHub) for transparency and compliance.
            Your continued use of the platform after changes constitutes acceptance of the updated policy.
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>15. Contact Us</h2>
          <p style={styles.text}>
            For privacy-related questions, data access requests, or concerns:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Visit our <a href="/contact-us" style={styles.link}>Contact Us</a> page</li>
            <li style={styles.listItem}>Use our <a href="/help-center" style={styles.link}>Help Center</a></li>
          </ul>
          <p style={styles.text}>
            <strong>For payment data inquiries:</strong> Contact Stripe directly at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={styles.link}>https://stripe.com/privacy</a>
          </p>
          <p style={styles.text}>
            <strong>Response Time:</strong> We aim to respond to privacy requests within 30 days.
          </p>
          <p style={styles.text}>
            <strong>Data Requests:</strong> For account deletion or data export requests, please <a href="/contact-us" style={styles.link}>contact us</a> directly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
